/**
 * Tax knowledge-base chat API.
 *
 * Endpoint (verified via curl):
 *   POST {base}/ask
 *     body: { query: string, top_k: number }
 *     200:  { query, answer (markdown), citations[], metadata }
 *
 * The RAG bot is now hosted inside the main backend under /api/v1/bot.
 * `base` is derived from VITE_API_URL so dev/prod share a single source of
 * truth for the backend URL.
 *
 * The endpoint is RAG-backed and can take ~20-30s end-to-end. Callers must
 * not impose a short fetch timeout. We don't use the shared apiClient
 * because (a) no auth header is needed and (b) we want a plain fetch with
 * an AbortController the chat widget can cancel on unmount.
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
const CHAT_API_BASE = `${API_URL.replace(/\/$/, '')}/api/v1/bot`;

/**
 * Thrown when the chat API returns a non-2xx response.
 *
 * `message` is already user-safe (mapped from status). The raw backend `detail`
 * is preserved on the instance for debugging but should not be rendered to
 * users — the backend may include exception-class names or other detail that
 * shouldn't reach the widget bubble.
 */
export class ChatApiError extends Error {
  readonly status: number;
  readonly detail?: string;
  constructor(status: number, detail?: string) {
    super(ChatApiError.friendlyMessage(status));
    this.name = 'ChatApiError';
    this.status = status;
    this.detail = detail;
  }
  static friendlyMessage(status: number): string {
    if (status === 429)
      return "I'm handling a lot of questions right now — give me a moment and try again.";
    if (status === 413)
      return "That's a bit more than I can take in at once. Could you trim it down and try again?";
    if (status === 400)
      return "I didn't quite catch that. Could you rephrase it for me?";
    if (status === 401 || status === 403)
      return "I'm not able to access that right now. Please check back shortly.";
    if (status === 404)
      return "I couldn't find what you're looking for. Want to try a different question?";
    if (status === 408 || status === 504)
      return "That took longer than expected on my end. Mind trying once more?";
    if (status >= 500 && status < 600)
      return "I'm having a little trouble reaching my knowledge base. Please try again in a moment.";
    return "Something didn't go through. Please try again.";
  }
}

/** Thrown when the chat API can't be reached (offline, DNS, CORS, etc.). */
export class ChatNetworkError extends Error {
  constructor() {
    super(
      "I can't seem to reach my knowledge base right now. Check your connection and try again in a moment.",
    );
    this.name = 'ChatNetworkError';
  }
}

export interface ChatCitation {
  node_id: string;
  title: string;
  section_path: string;
}

export interface AskMetadata {
  total_latency_ms?: number;
  retrieval_latency_ms?: number;
  generation_latency_ms?: number;
  total_tokens?: number;
  retrieval_tokens?: number;
  generation_tokens?: number;
  retrieval_model?: string;
  generation_model?: string;
}

export interface AskResponse {
  query: string;
  answer: string;
  citations: ChatCitation[];
  metadata: AskMetadata;
}

export interface AskOptions {
  topK?: number;
  signal?: AbortSignal;
}

export async function askChat(
  query: string,
  { topK = 5, signal }: AskOptions = {},
): Promise<AskResponse> {
  const trimmed = query.trim();
  if (!trimmed) throw new Error('Query is empty');

  let res: Response;
  try {
    res = await fetch(`${CHAT_API_BASE}/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ query: trimmed, top_k: topK }),
      signal,
    });
  } catch (e) {
    // Preserve cancellation so the widget can ignore it silently.
    if ((e as DOMException)?.name === 'AbortError') throw e;
    throw new ChatNetworkError();
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new ChatApiError(res.status, detail || undefined);
  }

  return (await res.json()) as AskResponse;
}
